import { ApiError, NetworkError } from './errors.js';
import { clearSession, getAccessToken, setSession } from './session-store.js';
import type { AcceptedResponse, MeResponse, SessionResponse } from './types.js';

const API_BASE = '/api';
const CSRF_COOKIE = 'kitup_csrf';
const CSRF_HEADER = 'x-kitup-csrf';
export const ADMIN_OWNER_HEADER = 'x-kitup-admin-owner-id';

/** Владелец книги кодируется в отдельном админском URL и не сохраняется в сессии. */
export function adminOwnerIdFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/admin\/accounts\/([^/]+)\/books\/[^/]+(?:\/|$)/u);
  if (!match?.[1]) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}

interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  readonly body?: unknown;
  /** Эндпоинты на cookie требуют парный CSRF-заголовок (01-SPEC.md §7). */
  readonly withCsrf?: boolean;
  /** Внутренний флаг: запрос уже повторялся после обновления токена. */
  readonly retried?: boolean;
}

function readCsrfToken(): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${CSRF_COOKIE}=([^;]*)`));
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

async function parseError(response: Response): Promise<ApiError> {
  try {
    const payload: unknown = await response.json();
    const error = (payload as { error?: { code?: string; message?: string } }).error;
    return new ApiError(
      response.status,
      error?.code ?? 'unknown',
      error?.message ?? 'Unexpected API error',
    );
  } catch {
    return new ApiError(response.status, 'unknown', 'Unexpected API error');
  }
}

/**
 * Обновление токена — единственный запрос, который может идти одновременно
 * из нескольких мест. Ротация одноразовая, поэтому два параллельных обновления
 * одним токеном выглядят для сервера как переиспользование украденной копии.
 *
 * Защит две, и они дополняют друг друга:
 *
 * 1. `refreshInFlight` — внутри одной вкладки параллельные вызовы ждут один промис.
 * 2. Web Locks — между вкладками. Вторая вкладка входит в критическую секцию
 *    только после первой, а к этому моменту cookie уже содержит новый токен,
 *    и её ротация проходит штатно, ничего не гася.
 *
 * Там, где Web Locks недоступны, страховкой служит окно доброкачественной гонки
 * на сервере (`ROTATION_GRACE_MS` в `apps/api/src/modules/auth/service.ts`).
 */
let refreshInFlight: Promise<SessionResponse | null> | null = null;

const REFRESH_LOCK = 'kitup-auth-refresh';

function withCrossTabLock<T>(run: () => Promise<T>): Promise<T> {
  const locks: LockManager | undefined = globalThis.navigator?.locks;
  return locks ? locks.request(REFRESH_LOCK, run) : run();
}

async function performRefresh(): Promise<SessionResponse | null> {
  try {
    const session = await request<SessionResponse>('/auth/refresh', {
      method: 'POST',
      withCsrf: true,
      retried: true,
    });
    setSession(session.accessToken, session.user);
    return session;
  } catch (error: unknown) {
    // Только явный 401 означает, что refresh-токен истёк или отозван.
    // Сетевая ошибка и 5xx во время рестарта dev/prod-сервера не завершают
    // сессию: cookie всё ещё жив, повторный refresh сможет её восстановить.
    if (error instanceof ApiError && error.status === 401) {
      clearSession();
      return null;
    }
    throw error;
  }
}

export async function refreshSession(): Promise<SessionResponse | null> {
  refreshInFlight ??= withCrossTabLock(performRefresh).finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, withCsrf = false, retried = false } = options;

  const headers: Record<string, string> = {};
  if (body !== undefined) headers['content-type'] = 'application/json';

  const accessToken = getAccessToken();
  if (accessToken) headers.authorization = `Bearer ${accessToken}`;

  const adminOwnerId =
    !path.startsWith('/admin/') && !path.startsWith('/auth/')
      ? adminOwnerIdFromPath(globalThis.location?.pathname ?? '')
      : null;
  if (adminOwnerId) headers[ADMIN_OWNER_HEADER] = adminOwnerId;

  if (withCsrf) {
    const csrf = readCsrfToken();
    if (csrf) headers[CSRF_HEADER] = csrf;
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      credentials: 'same-origin',
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch {
    throw new NetworkError('Network request failed');
  }

  if (response.ok) {
    return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
  }

  // Тихое обновление: истёкший access-токен не должен показываться пользователю.
  if (response.status === 401 && !retried) {
    const session = await refreshSession();
    if (session) return request<T>(path, { ...options, retried: true });
  }

  throw await parseError(response);
}

export const authApi = {
  registrationStatus: () => request<{ registrationEnabled: boolean }>('/auth/registration'),

  /**
   * `termsVersion` is not form input — it is the edition of the texts this build
   * is showing, and the record of what the person accepted (§6.4).
   */
  register: (body: {
    email: string;
    password: string;
    name: string;
    acceptedTerms: true;
    adultAck: true;
    termsVersion: string;
  }) => request<AcceptedResponse>('/auth/register', { method: 'POST', body }),

  login: (body: { email: string; password: string }) =>
    request<SessionResponse>('/auth/login', { method: 'POST', body }),

  logout: () => request<AcceptedResponse>('/auth/logout', { method: 'POST', withCsrf: true }),
  /** Необратимое удаление аккаунта: пароль подтверждает намерение (E16-T4). */
  deleteAccount: (password: string) =>
    request<{ status: 'deleted' }>('/me', { method: 'DELETE', body: { password } }),

  updateLocale: (locale: 'ru' | 'en') =>
    request<{ locale: 'ru' | 'en' }>('/me', { method: 'PATCH', body: { locale } }),

  updateName: (name: string) =>
    request<{ name: string }>('/me', { method: 'PATCH', body: { name } }),

  me: () => request<MeResponse>('/auth/me'),

  verifyEmail: (token: string) =>
    request<MeResponse>('/auth/verify-email', { method: 'POST', body: { token } }),

  resendVerification: () =>
    request<AcceptedResponse>('/auth/resend-verification', { method: 'POST' }),

  forgotPassword: (email: string) =>
    request<AcceptedResponse>('/auth/forgot-password', { method: 'POST', body: { email } }),

  resetPassword: (token: string, password: string) =>
    request<AcceptedResponse>('/auth/reset-password', {
      method: 'POST',
      body: { token, password },
    }),
};
